import { useEffect, useState } from 'react';
import { Shield, FileText, AlertTriangle, Download, RefreshCw, CheckCircle, XCircle, Eye, Search, ChevronRight, Lock, Calendar } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

// ── API helpers ────────────────────────────────────────────────────────────────
async function apiFetch(token, path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Error ${res.status}`); }
  return res.json();
}

async function downloadBlob(token, path, filename) {
  const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Download failed'); }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

const SEVERITY_COLORS = {
  critical: 'text-red-400 bg-red-500/10 border-red-500/20',
  warning:  'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  info:     'text-blue-400 bg-blue-500/10 border-blue-500/20',
};

const STATUS_COLORS = {
  open:     'text-red-400',
  resolved: 'text-green-400',
  ignored:  'text-zinc-500',
};

// ── Tab: A&D Report ────────────────────────────────────────────────────────────
function ADReportTab() {
  const { token } = useAdminAuth();
  const [filters, setFilters] = useState({ date_from: '', date_to: '', nfa_only: false, category: '', disposition_status: '' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');

  const buildQS = () => {
    const p = new URLSearchParams();
    if (filters.date_from) p.append('date_from', filters.date_from);
    if (filters.date_to) p.append('date_to', filters.date_to);
    if (filters.nfa_only) p.append('nfa_only', 'true');
    if (filters.category) p.append('category', filters.category);
    if (filters.disposition_status) p.append('disposition_status', filters.disposition_status);
    return p.toString();
  };

  const handlePreview = async () => {
    setLoading(true); setError('');
    try {
      const data = await apiFetch(token, `/api/admin/compliance/ad-report?${buildQS()}`);
      setRows(data.rows || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleDownload = async (fmt, mode = 'plain') => {
    setDownloading(fmt + mode); setError('');
    try {
      const qs = buildQS();
      if (fmt === 'pdf') {
        await downloadBlob(token, `/api/admin/compliance/ad-report/pdf?${qs}&mode=${mode}`,
          `WM_AD_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      } else {
        await downloadBlob(token, `/api/admin/compliance/ad-report/csv?${qs}`,
          `WM_AD_Report_${new Date().toISOString().slice(0,10)}.csv`);
      }
    } catch (e) { setError(e.message); }
    setDownloading('');
  };

  const inputCls = "bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors";
  const labelCls = "block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1";

  return (
    <div data-testid="ad-report-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bebas text-2xl text-white tracking-wider">ACQUISITION &amp; DISPOSITION REPORT</h2>
          <p className="font-inter text-xs text-zinc-500 mt-0.5">ATF-style A&D logbook from serial number and order data</p>
        </div>
      </div>

      <div className="bg-orange-500/10 border border-orange-500/30 p-3 mb-4">
        <p className="font-inter text-xs text-orange-300"><strong>Disclaimer:</strong> For internal recordkeeping support only. Does NOT constitute official ATF forms or legal compliance documentation.</p>
      </div>

      {/* Filters */}
      <div className="bg-[#1a1a1a] border border-white/10 p-5 mb-4">
        <h3 className="font-bebas text-lg text-white tracking-wider mb-4">FILTERS</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div><label className={labelCls}>From Date</label><input type="date" className={inputCls + " w-full"} value={filters.date_from} onChange={e => setFilters({...filters, date_from: e.target.value})} /></div>
          <div><label className={labelCls}>To Date</label><input type="date" className={inputCls + " w-full"} value={filters.date_to} onChange={e => setFilters({...filters, date_to: e.target.value})} /></div>
          <div>
            <label className={labelCls}>Category</label>
            <select className={inputCls + " w-full"} value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})}>
              <option value="">All Categories</option>
              {['suppressors','sme','clamp-ons','muzzle-devices'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select className={inputCls + " w-full"} value={filters.disposition_status} onChange={e => setFilters({...filters, disposition_status: e.target.value})}>
              <option value="">All Status</option>
              <option value="in_inventory">In Inventory</option>
              <option value="assigned">Assigned</option>
              <option value="shipped">Shipped/Disposed</option>
            </select>
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 cursor-pointer mb-1">
              <input type="checkbox" checked={filters.nfa_only} onChange={e => setFilters({...filters, nfa_only: e.target.checked})} className="accent-orange-500" />
              <span className="font-inter text-sm text-zinc-300">NFA Only</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button data-testid="ad-preview-button" onClick={handlePreview} disabled={loading}
            className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white font-inter text-sm px-4 py-2.5 transition-colors">
            <Search size={14} />{loading ? 'Loading...' : 'Preview Records'}
          </button>
          <button data-testid="ad-download-pdf" onClick={() => handleDownload('pdf', 'plain')} disabled={!!downloading}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-inter text-sm px-4 py-2.5 transition-colors">
            <Download size={14} />{downloading === 'pdfplain' ? '...' : 'PDF (Print)'}
          </button>
          <button data-testid="ad-download-pdf-branded" onClick={() => handleDownload('pdf', 'branded')} disabled={!!downloading}
            className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white font-inter text-sm px-4 py-2.5 transition-colors">
            <Download size={14} />{downloading === 'pdfbranded' ? '...' : 'PDF (Branded)'}
          </button>
          <button data-testid="ad-download-csv" onClick={() => handleDownload('csv')} disabled={!!downloading}
            className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white font-inter text-sm px-4 py-2.5 transition-colors">
            <Download size={14} />{downloading === 'csvundefined' ? '...' : 'CSV Export'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">{error}</div>}

      {/* Preview table */}
      {rows.length > 0 && (
        <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
          <div className="px-5 py-3 border-b border-white/10">
            <span className="font-bebas text-lg text-white tracking-wider">PREVIEW — {rows.length} Records</span>
          </div>
          <table className="w-full" data-testid="ad-report-table">
            <thead>
              <tr className="border-b border-white/10">
                {['Serial #','Product','Type','Mfg Date','Marked','Disposed To','Disp. Date','Ref','Status'].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-inter text-[9px] text-zinc-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.slice(0, 50).map((r, i) => (
                <tr key={i} className="hover:bg-white/2">
                  <td className="px-3 py-2 font-mono text-xs text-orange-400 font-bold">{r.serial_number}</td>
                  <td className="px-3 py-2 font-inter text-xs text-zinc-300">{r.product_name}</td>
                  <td className="px-3 py-2 font-inter text-xs">{r.nfa_item ? <span className="text-orange-400 font-bold">NFA</span> : 'Std'}</td>
                  <td className="px-3 py-2 font-inter text-xs text-zinc-400">{r.manufacture_date?.slice(0,10) || '—'}</td>
                  <td className="px-3 py-2 text-center">{r.marking_date ? <CheckCircle size={12} className="text-green-400 mx-auto" /> : <XCircle size={12} className="text-red-400 mx-auto" />}</td>
                  <td className="px-3 py-2 font-inter text-xs text-zinc-400">{r.disposed_to_name || '—'}</td>
                  <td className="px-3 py-2 font-inter text-xs text-zinc-400">{r.disposition_date?.slice(0,10) || '—'}</td>
                  <td className="px-3 py-2 font-inter text-xs font-mono text-zinc-500">{r.order_number || '—'}</td>
                  <td className="px-3 py-2 font-inter text-xs text-zinc-400">{r.status}</td>
                </tr>
              ))}
              {rows.length > 50 && <tr><td colSpan={9} className="px-3 py-2 text-center font-inter text-xs text-zinc-600">...{rows.length - 50} more records — download CSV for full export</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab: Exceptions Queue ──────────────────────────────────────────────────────
function ExceptionsTab() {
  const { token } = useAdminAuth();
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');

  const load = () => {
    apiFetch(token, '/api/admin/compliance/exceptions')
      .then(setExceptions).catch(e => setError(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleScan = async () => {
    setScanning(true); setError('');
    try {
      const result = await apiFetch(token, '/api/admin/compliance/exceptions/scan', { method: 'POST' });
      await load();
      if (result.new_exceptions === 0) setError('Scan complete — no new exceptions found.');
    } catch (e) { setError(e.message); }
    setScanning(false);
  };

  const handleResolve = async (id, status) => {
    try {
      await apiFetch(token, `/api/admin/compliance/exceptions/${id}/resolve`, {
        method: 'PUT', body: JSON.stringify({ status, notes: '' })
      });
      setExceptions(prev => prev.map(e => e.exception_id === id ? {...e, status} : e));
    } catch (e) { setError(e.message); }
  };

  const filtered = filterStatus === 'all' ? exceptions : exceptions.filter(e => e.status === filterStatus);
  const counts = { open: exceptions.filter(e => e.status === 'open').length,
    resolved: exceptions.filter(e => e.status === 'resolved').length,
    critical: exceptions.filter(e => e.severity === 'critical' && e.status === 'open').length };

  return (
    <div data-testid="exceptions-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bebas text-2xl text-white tracking-wider">COMPLIANCE EXCEPTIONS</h2>
          <p className="font-inter text-xs text-zinc-500 mt-0.5">Auto-detected compliance issues — resolve before inspection</p>
        </div>
        <button data-testid="scan-exceptions-button" onClick={handleScan} disabled={scanning}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-inter text-sm px-4 py-2.5 transition-colors">
          <RefreshCw size={14} className={scanning ? 'animate-spin' : ''} />{scanning ? 'Scanning...' : 'Scan for Issues'}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Open Issues', val: counts.open, cls: 'text-red-400' },
          { label: 'Critical', val: counts.critical, cls: 'text-orange-400' },
          { label: 'Resolved', val: counts.resolved, cls: 'text-green-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#1a1a1a] border border-white/10 p-4 text-center">
            <div className={`font-bebas text-3xl tracking-wider ${s.cls}`}>{s.val}</div>
            <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {['open', 'resolved', 'ignored', 'all'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`font-inter text-xs px-3 py-1.5 border uppercase tracking-wider transition-colors ${filterStatus === s ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'}`}>
            {s}
          </button>
        ))}
      </div>

      {error && <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-inter text-sm px-4 py-3 mb-4">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-[#1a1a1a] border border-white/10">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={36} className="text-green-500 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-500 tracking-wider">
                {filterStatus === 'open' ? 'NO OPEN EXCEPTIONS' : 'NO RECORDS'}
              </p>
              <p className="font-inter text-sm text-zinc-600 mt-1">
                {filterStatus === 'open' ? 'Run a scan to check for compliance issues' : `No ${filterStatus} exceptions found`}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map(exc => (
                <div key={exc.exception_id || exc._id} data-testid={`exception-${exc.exception_id}`} className="flex items-start gap-4 px-5 py-4">
                  <div className={`font-inter text-[10px] font-bold px-2 py-1 border uppercase tracking-wider flex-shrink-0 mt-0.5 ${SEVERITY_COLORS[exc.severity] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                    {exc.severity}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-inter text-xs font-bold font-mono text-orange-400">{exc.issue_code}</span>
                      <span className="font-inter text-[10px] text-zinc-600">{exc.object_type}: {exc.object_id}</span>
                    </div>
                    <p className="font-inter text-sm text-zinc-300">{exc.description}</p>
                    {exc.detected_at && <p className="font-inter text-[10px] text-zinc-600 mt-0.5">Detected: {new Date(exc.detected_at).toLocaleString()}</p>}
                  </div>
                  <div className={`font-inter text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${STATUS_COLORS[exc.status] || 'text-zinc-400'}`}>
                    {exc.status}
                  </div>
                  {exc.status === 'open' && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`resolve-${exc.exception_id}`}
                        onClick={() => handleResolve(exc.exception_id, 'resolved')}
                        className="font-inter text-[10px] text-green-400 border border-green-500/30 bg-green-500/10 hover:bg-green-500/20 px-2 py-1 transition-colors">
                        Resolve
                      </button>
                      <button onClick={() => handleResolve(exc.exception_id, 'ignored')}
                        className="font-inter text-[10px] text-zinc-500 border border-white/10 hover:border-white/30 px-2 py-1 transition-colors">
                        Ignore
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: Inspection Packet ─────────────────────────────────────────────────────
function InspectionPacketTab() {
  const { token } = useAdminAuth();
  const [scope, setScope] = useState('full');
  const [scopeValue, setScopeValue] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [packets, setPackets] = useState([]);
  const [currentPacket, setCurrentPacket] = useState(null);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(token, '/api/admin/compliance/inspection-packets').then(setPackets).catch(console.error);
  }, [token]);

  const handleGenerate = async () => {
    setGenerating(true); setError('');
    try {
      const body = { scope, scope_value: scopeValue || null, date_from: dateFrom || null, date_to: dateTo || null };
      const pkt = await apiFetch(token, '/api/admin/compliance/inspection-packet/generate', {
        method: 'POST', body: JSON.stringify(body)
      });
      setCurrentPacket(pkt);
      setPackets(prev => [pkt, ...prev]);
    } catch (e) { setError(e.message); }
    setGenerating(false);
  };

  const handleDownload = async (packetId, mode) => {
    setDownloading(packetId + mode);
    try { await downloadBlob(token, `/api/admin/compliance/inspection-packets/${packetId}/pdf?mode=${mode}`, `WM_InspectionPacket_${packetId}.pdf`); }
    catch (e) { setError(e.message); }
    setDownloading('');
  };

  const inputCls = "bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors";

  return (
    <div data-testid="inspection-packet-tab">
      <div className="mb-4">
        <h2 className="font-bebas text-2xl text-white tracking-wider">INSPECTION PACKET GENERATOR</h2>
        <p className="font-inter text-xs text-zinc-500 mt-0.5">Generate consolidated compliance reference packets for internal review or audit preparation</p>
      </div>

      <div className="bg-orange-500/10 border border-orange-500/30 p-3 mb-4">
        <p className="font-inter text-xs text-orange-300">For internal reference only. Not a substitute for official ATF records or legal counsel.</p>
      </div>

      {/* Config */}
      <div className="bg-[#1a1a1a] border border-white/10 p-5 mb-4">
        <h3 className="font-bebas text-lg text-white tracking-wider mb-4">PACKET SCOPE</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          {[
            { val: 'full', label: 'Full System' },
            { val: 'date_range', label: 'Date Range' },
            { val: 'order', label: 'Single Order' },
            { val: 'serial', label: 'Single Serial' },
          ].map(s => (
            <button key={s.val} onClick={() => setScope(s.val)} data-testid={`scope-${s.val}`}
              className={`font-inter text-sm py-2.5 border transition-colors ${scope === s.val ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
              {s.label}
            </button>
          ))}
        </div>
        {scope === 'date_range' && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">From</label><input type="date" className={inputCls + " w-full"} value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
            <div><label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">To</label><input type="date" className={inputCls + " w-full"} value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
          </div>
        )}
        {(scope === 'order' || scope === 'serial') && (
          <div className="mb-3"><label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{scope === 'order' ? 'Order Number' : 'Serial Number'}</label>
            <input className={inputCls + " w-full"} value={scopeValue} onChange={e => setScopeValue(e.target.value)} placeholder={scope === 'order' ? 'WM-XXXXXXXX' : 'WM-SUPP-XXXX-XXXXXX'} /></div>
        )}
        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-3">{error}</div>}
        <button data-testid="generate-inspection-packet" onClick={handleGenerate} disabled={generating}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-inter text-sm font-medium px-5 py-2.5 transition-colors">
          <FileText size={14} />{generating ? 'Generating...' : 'Generate Inspection Packet'}
        </button>
      </div>

      {/* Current packet summary */}
      {currentPacket && (
        <div className="bg-[#1a1a1a] border border-orange-500/30 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="font-bebas text-xl text-white tracking-wider">{currentPacket.packet_id}</span>
              <span className="font-inter text-xs text-zinc-500 ml-3">{currentPacket.scope_desc}</span>
            </div>
            <div className="flex gap-2">
              <button data-testid="download-inspection-pdf-plain" onClick={() => handleDownload(currentPacket.packet_id, 'plain')} disabled={!!downloading}
                className="flex items-center gap-1.5 font-inter text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-2 transition-colors">
                <Download size={12} />{downloading.includes('plain') ? '...' : 'PDF (Physical)'}
              </button>
              <button data-testid="download-inspection-pdf-branded" onClick={() => handleDownload(currentPacket.packet_id, 'branded')} disabled={!!downloading}
                className="flex items-center gap-1.5 font-inter text-xs bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white px-3 py-2 transition-colors">
                <Download size={12} />{downloading.includes('branded') ? '...' : 'PDF (Branded)'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {Object.entries(currentPacket.summary).map(([k, v]) => (
              <div key={k} className="bg-[#141414] border border-white/5 p-2 text-center">
                <div className={`font-bebas text-xl tracking-wider ${v > 0 && k.includes('exception') ? 'text-orange-400' : v > 0 && k.includes('critical') ? 'text-red-400' : 'text-white'}`}>{v}</div>
                <div className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider">{k.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      {packets.length > 0 && (
        <div className="bg-[#1a1a1a] border border-white/10">
          <div className="px-5 py-3 border-b border-white/10"><span className="font-bebas text-lg text-white tracking-wider">PACKET HISTORY</span></div>
          <div className="divide-y divide-white/5">
            {packets.slice(0, 10).map(p => (
              <div key={p.packet_id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <span className="font-inter text-sm text-white">{p.packet_id}</span>
                  <span className="font-inter text-xs text-zinc-500 ml-3">{p.scope_desc}</span>
                  <span className="font-inter text-[10px] text-zinc-600 ml-3">{p.generated_at?.slice(0,10)}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleDownload(p.packet_id, 'plain')} disabled={!!downloading}
                    className="font-inter text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1.5 transition-colors flex items-center gap-1 disabled:opacity-40">
                    <Download size={9} />PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Daily Manifest ────────────────────────────────────────────────────────
function DailyManifestTab() {
  const { token } = useAdminAuth();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/admin/compliance/daily-manifest/pdf?date=${date}&mode=plain`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      // Just validate it works — show stats from a text endpoint instead
      setPreview({ ok: true, date });
    } catch (e) {
      setPreview({ ok: false, error: e.message });
    }
    setLoading(false);
  };

  const handleDownload = async (mode) => {
    setDownloading(mode);
    try {
      await downloadBlob(token, `/api/admin/compliance/daily-manifest/pdf?date=${date}&mode=${mode}`, `WM_DailyManifest_${date}.pdf`);
    } catch (e) { alert(e.message); }
    setDownloading(false);
  };

  return (
    <div data-testid="daily-manifest-tab">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="font-bebas text-2xl text-white tracking-wider flex items-center gap-3">
            DAILY MANIFEST <Calendar size={18} className="text-orange-500" />
          </h2>
          <p className="font-inter text-xs text-zinc-500 mt-0.5">End-of-day ATF A&amp;D log reconciliation — all shipments for a given date in one printable document</p>
        </div>
      </div>

      <div className="bg-orange-500/10 border border-orange-500/30 p-4 mb-6">
        <p className="font-inter text-xs text-orange-300">
          <strong>ATF A&amp;D Reconciliation:</strong> This manifest lists every firearm/suppressor shipped on the selected date with serial numbers, tracking info, and disposition records. Print and file daily with your bound book. Federal records retention requirements apply.
        </p>
      </div>

      {/* Date picker + download */}
      <div className="bg-[#1a1a1a] border border-white/10 p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="flex-1">
            <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">Manifest Date</label>
            <input
              type="date"
              value={date}
              onChange={e => { setDate(e.target.value); setPreview(null); }}
              data-testid="manifest-date-input"
              className="w-full bg-[#111] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="preview-manifest-btn"
              onClick={loadPreview}
              disabled={loading}
              className="flex items-center gap-2 border border-white/20 hover:border-orange-500/40 text-zinc-400 hover:text-white font-inter text-sm px-4 py-3 transition-colors disabled:opacity-60"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Checking...' : 'Preview'}
            </button>
            <button
              data-testid="download-manifest-plain"
              onClick={() => handleDownload('plain')}
              disabled={!!downloading}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-5 py-3 transition-colors"
            >
              <Download size={16} />
              {downloading === 'plain' ? 'GENERATING...' : 'DOWNLOAD MANIFEST PDF'}
            </button>
          </div>
        </div>

        {preview && (
          <div className={`mt-4 p-3 border font-inter text-sm ${preview.ok ? 'border-green-500/30 bg-green-500/10 text-green-400' : 'border-red-500/30 bg-red-500/10 text-red-400'}`}>
            {preview.ok
              ? `✓ Manifest ready for ${preview.date} — click Download to generate the PDF`
              : `Error: ${preview.error}`
            }
          </div>
        )}
      </div>

      {/* What's included */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-[#1a1a1a] border border-white/10 p-5">
          <div className="font-bebas text-lg text-white tracking-wider mb-3">MANIFEST INCLUDES</div>
          <div className="space-y-2">
            {[
              'Cover page with daily totals (orders, serials, NFA count)',
              'Per-shipment breakdown with tracking numbers',
              'Complete serial number list with manufacture & marking dates',
              'ATF A&D disposition summary table (all serials shipped)',
              'End-of-day certification with signature blocks',
              'NFA vs Non-NFA item breakdown',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle size={12} className="text-orange-500 mt-0.5 flex-shrink-0" />
                <span className="font-inter text-xs text-zinc-400">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#1a1a1a] border border-white/10 p-5">
          <div className="font-bebas text-lg text-white tracking-wider mb-3">QUICK ACCESS</div>
          <div className="space-y-2">
            {[
              { label: "Today's Manifest", action: () => { setDate(new Date().toISOString().split('T')[0]); } },
              { label: "Yesterday's Manifest", action: () => { const d = new Date(); d.setDate(d.getDate()-1); setDate(d.toISOString().split('T')[0]); } },
              { label: "This Week (Mon)", action: () => { const d = new Date(); const day = d.getDay(); d.setDate(d.getDate() - (day || 7) + 1); setDate(d.toISOString().split('T')[0]); } },
            ].map((item, i) => (
              <button key={i} onClick={item.action}
                className="w-full text-left flex items-center justify-between font-inter text-xs text-zinc-400 hover:text-orange-400 border border-white/10 hover:border-orange-500/30 px-3 py-2 transition-colors">
                {item.label} <ChevronRight size={11} />
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="font-inter text-[10px] text-zinc-600">Tip: Generate and print a manifest at end of each business day. File with your ATF bound book entries.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Inspector Mode ────────────────────────────────────────────────────────
function InspectorModeTab() {
  const [active, setActive] = useState(false);
  return (
    <div data-testid="inspector-mode-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-bebas text-2xl text-white tracking-wider flex items-center gap-3">
            INSPECTOR MODE <Lock size={18} className="text-orange-500" />
          </h2>
          <p className="font-inter text-xs text-zinc-500 mt-0.5">Read-only view for audit scenarios — no edits possible in this mode</p>
        </div>
        <button onClick={() => setActive(!active)} data-testid="inspector-mode-toggle"
          className={`flex items-center gap-2 font-inter text-sm font-medium px-4 py-2.5 border transition-colors ${active ? 'bg-orange-500 border-orange-500 text-white' : 'border-white/20 text-zinc-400 hover:border-orange-500/40'}`}>
          <Eye size={14} />{active ? 'Exit Inspector Mode' : 'Enable Inspector Mode'}
        </button>
      </div>

      {active ? (
        <div className="bg-orange-500/10 border border-orange-500/30 p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Eye size={16} className="text-orange-500" />
            <span className="font-bebas text-lg text-orange-400 tracking-wider">INSPECTOR MODE ACTIVE — READ ONLY</span>
          </div>
          <p className="font-inter text-xs text-zinc-400">All edits are disabled. Navigate to compliance sections below for read-only review.</p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: 'A&D Report', desc: 'Full acquisition and disposition log', tab: 'ad_report', icon: FileText },
          { label: 'Compliance Exceptions', desc: 'Open issues and exception log', tab: 'exceptions', icon: AlertTriangle },
          { label: 'Inspection Packet', desc: 'Generate or view inspection packets', tab: 'packet', icon: Shield },
          { label: 'Daily Manifest', desc: 'End-of-day ATF A&D log reconciliation', tab: 'manifest', icon: Calendar },
          { label: 'Serialization', desc: 'Serial trace and assignment history', href: '/admin/serialization', icon: Shield },
          { label: 'Audit Log', desc: 'Immutable event ledger', href: '/admin/audit', icon: Lock },
          { label: 'Orders', desc: 'Full order pipeline view', href: '/admin/orders', icon: FileText },
        ].map(item => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="bg-[#1a1a1a] border border-white/10 hover:border-orange-500/30 p-5 transition-colors">
              <Icon size={18} className="text-orange-500 mb-3" />
              <div className="font-bebas text-lg text-white tracking-wider mb-1">{item.label}</div>
              <div className="font-inter text-xs text-zinc-500 mb-3">{item.desc}</div>
              {item.href ? (
                <a href={item.href} className="font-inter text-xs text-orange-500 hover:text-white transition-colors">
                  Open → {active ? '' : ''}
                </a>
              ) : (
                <span className="font-inter text-xs text-orange-500">View in Compliance Section →</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Compliance Page ───────────────────────────────────────────────────────
export default function AdminCompliance() {
  const [tab, setTab] = useState('ad_report');

  const TABS = [
    { key: 'ad_report', label: 'A&D Report', icon: FileText },
    { key: 'exceptions', label: 'Exceptions Queue', icon: AlertTriangle },
    { key: 'packet', label: 'Inspection Packet', icon: Shield },
    { key: 'manifest', label: 'Daily Manifest', icon: Calendar },
    { key: 'inspector', label: 'Inspector Mode', icon: Eye },
  ];

  return (
    <div data-testid="admin-compliance-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-bebas text-4xl text-white tracking-wider">COMPLIANCE CENTER</h1>
          <span className="font-inter text-[10px] font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 uppercase tracking-wider">ATF-Ready</span>
        </div>
        <p className="font-inter text-sm text-zinc-500 mt-1">A&D reporting, serial traceability, inspection packets, compliance exceptions</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`compliance-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 font-inter text-sm px-4 py-3 transition-colors border-b-2 -mb-px ${tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
              <Icon size={14} />{t.label}
            </button>
          );
        })}
      </div>

      {tab === 'ad_report' && <ADReportTab />}
      {tab === 'exceptions' && <ExceptionsTab />}
      {tab === 'packet' && <InspectionPacketTab />}
      {tab === 'manifest' && <DailyManifestTab />}
      {tab === 'inspector' && <InspectorModeTab />}
    </div>
  );
}
